import React, { useState } from "react";
import EditEmployment from "./EditEmployment";

// Employment dates arrive as date-only strings ("2023-06-01") or as the API's UTC-stamped timestamps;
// either way "2023-06-01" parses as UTC midnight, so format in UTC or every viewer west of Greenwich
// reads the date a day early. Anything unparseable is shown as-is rather than as "Invalid Date".
function formattedDate(value) {
  if (!value) {
    return null;
  }

  const options = { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" };
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString(undefined, options);
}

function EditEmploymentLine({ employment, onChange, onRemove, onSetFeatured, disabled = false }) {
  const [editing, setEditing] = useState(false);

  const toggleEditing = () => {
    setEditing((prev) => !prev);
  };

  const confirmEditing = (updatedEmployment) => {
    setEditing(false);
    onChange(updatedEmployment);
  };

  // Hiding rides the ordinary update path (person_hidden is a plain writable column); featuring goes
  // through onSetFeatured because person_featured is flip-only on the server. The two are independent:
  // a hidden job keeps its star, but the title derivation ignores hidden jobs, so the star only takes
  // effect again if the job is unhidden.
  const toggleHidden = () => {
    onChange({ ...employment, personHidden: !employment.personHidden });
  };

  return editing ? (
    <EditEmployment employment={employment} onConfirm={confirmEditing} onCancel={toggleEditing} disabled={disabled} />
  ) : (
    // Hidden rows stay listed (this is the surface for managing them) but render dimmed so their
    // absence from the show page is legible here.
    <tr className={employment.personHidden ? "opacity-50" : undefined}>
      <td>
        {employment.companyName}
        {/* Same badge vocabulary as the featured quote card — the solid star alone is easy to miss. */}
        {employment.featured && (
          <span className="qw-featured-badge badge rounded-pill d-inline-flex align-items-center fw-bold paragraph-sm ms-2">
            <i className="fas fa-star me-1" />
            Featured
          </span>
        )}
      </td>
      <td>{employment.title}</td>
      <td>{formattedDate(employment.from)}</td>
      <td>{formattedDate(employment.to)}</td>
      <td className="text-nowrap">
        <div className="d-flex gap-1">
          <button
            onClick={() => onSetFeatured(!employment.featured)}
            type="button"
            className="btn btn-sm btn-outline-secondary d-flex"
            aria-label={employment.featured ? "Unfeature" : "Feature"}
            title="Your featured job is the one shown as your title"
            disabled={disabled}
          >
            <i className={employment.featured ? "fas fa-star" : "far fa-star"} />
          </button>
          <button
            onClick={toggleHidden}
            type="button"
            className="btn btn-sm btn-outline-secondary d-flex"
            aria-label={employment.personHidden ? "Unhide" : "Hide"}
            title="Hidden jobs don't show on your profile"
            disabled={disabled}
          >
            <i className={employment.personHidden ? "fas fa-eye-slash" : "fas fa-eye"} />
          </button>
          <button
            onClick={toggleEditing}
            type="button"
            className="btn btn-sm btn-outline-primary d-flex"
            aria-label="Edit"
            disabled={disabled}
          >
            <i className="fas fa-edit" />
          </button>
          <button
            onClick={onRemove}
            type="button"
            className="btn btn-sm btn-outline-secondary d-flex"
            aria-label="Remove"
            disabled={disabled}
          >
            <i className="fas fa-trash text-destructive-90" />
          </button>
        </div>
      </td>
    </tr>
  );
}

export default EditEmploymentLine;
